import { useId, useMemo, useState } from 'react';
import { formatSignedTHB, formatTHB, toneClassFor } from '../../shared/format';
import { SectionLabel } from '../../shared/pixel';
import { useSnapshots, type Snapshot } from './useSnapshots';

/**
 * กราฟมูลค่าพอร์ตย้อนหลัง วาดด้วย SVG ล้วน — เหตุผลเดียวกับกราฟวงกลม
 * (ดู AllocationChart) คือไม่คุ้มที่จะเพิ่ม chart library ให้ bundle
 *
 * ดีไซน์ต้นแบบใช้กราฟแท่ง แต่ที่นี่ใช้เส้น+พื้นที่ใต้เส้นแทน เพราะแกนตั้งไม่ได้เริ่มจากศูนย์
 * (ดูเหตุผลที่ `toPoints`) — ความสูงของแท่งจะสื่อขนาดที่ไม่จริง ส่วนเส้นสื่อแค่ "ทิศทาง" ซึ่งตรงกว่า
 */

const RANGES = [
  { value: 30, label: '1M' },
  { value: 90, label: '3M' },
  { value: 180, label: '6M' },
  { value: 365, label: '1Y' },
  { value: 0, label: 'ALL' },
] as const;

const WIDTH = 600;
const HEIGHT = 170;
const PADDING = 4;

/** มูลค่าหนึ่งวันของเส้นที่กำลังวาด — ทั้งพอร์ต หรือแพลตฟอร์มเดียว */
interface Entry {
  date: string;
  valueTHB: string;
  isPartial: boolean;
}

interface Point {
  x: number;
  y: number;
  entry: Entry;
}

/**
 * เลือกมูลค่าที่จะวาดจาก snapshot
 *
 * แพลตฟอร์มใช้ `byPlatform` ที่ `dailySnapshot` เก็บไว้ทุกวันอยู่แล้ว วันที่แพลตฟอร์มนั้น
 * ยังไม่มีอยู่ถูกข้าม ไม่ใช่นับเป็นศูนย์ — ไม่งั้นเส้นจะดิ่งลงศูนย์ทั้งที่แค่ยังไม่ได้เปิดบัญชี
 */
function toEntries(snapshots: readonly Snapshot[], platform: string | null): Entry[] {
  if (platform === null) {
    return snapshots.map((s) => ({
      date: s.date,
      valueTHB: s.totalValueTHB,
      isPartial: s.isPartial,
    }));
  }

  return snapshots.flatMap((s) => {
    const group = s.byPlatform[platform];
    return group === undefined ? [] : [{ date: s.date, ...group }];
  });
}

/**
 * แปลงมูลค่าเป็นพิกัดบนกราฟ
 *
 * แกนตั้งอิงจากค่าต่ำสุด-สูงสุดของช่วงที่เลือก **ไม่ใช่จากศูนย์** — พอร์ตที่ขยับ 2%
 * บนสเกลที่เริ่มจากศูนย์จะดูเป็นเส้นตรงแบน มองไม่ออกว่าเกิดอะไรขึ้น
 *
 * แลกมาด้วยความเสี่ยงที่การขยับเล็ก ๆ จะดูใหญ่เกินจริง จึงต้องเขียนตัวเลขจริง
 * กำกับไว้ทั้งค่าสูงสุดและต่ำสุด ให้อ่านสเกลได้ ไม่ใช่ดูแต่รูปทรง
 */
function toPoints(entries: readonly Entry[]): { points: Point[]; min: number; max: number } {
  const values = entries.map((e) => Number(e.valueTHB));
  const min = Math.min(...values);
  const max = Math.max(...values);
  // พอร์ตที่มูลค่าเท่ากันทุกวันจะทำให้ span เป็นศูนย์แล้วหารไม่ได้ — วางเส้นไว้กลางกราฟ
  const span = max - min || 1;

  const points = entries.map((entry, index) => ({
    x:
      entries.length === 1
        ? WIDTH / 2
        : PADDING + (index / (entries.length - 1)) * (WIDTH - PADDING * 2),
    y:
      max === min
        ? HEIGHT / 2
        : HEIGHT - PADDING - ((Number(entry.valueTHB) - min) / span) * (HEIGHT - PADDING * 2),
    entry,
  }));

  return { points, min, max };
}

function formatDate(date: string): string {
  const [, month, day] = date.split('-');
  return `${day}/${month}`;
}

function RangePicker({ days, onChange }: { days: number; onChange: (days: number) => void }) {
  return (
    <div className="flex gap-1 rounded-lg border border-line-strong bg-sunken p-1">
      {RANGES.map((range) => (
        <button
          key={range.label}
          type="button"
          onClick={() => onChange(range.value)}
          className={[
            'rounded-[5px] px-2.5 py-1.5 font-mono text-xs transition',
            days === range.value
              ? 'bg-brand-sel text-brand-text'
              : 'text-ink-400 hover:text-ink-100',
          ].join(' ')}
        >
          {range.label}
        </button>
      ))}
    </div>
  );
}

/** `platform` เป็น null = ทั้งพอร์ต */
export function HistoryChart({ platform = null }: { platform?: string | null }) {
  const [days, setDays] = useState<number>(30);
  const query = useSnapshots(days);
  const gradientId = useId();

  const entries = useMemo(() => toEntries(query.data ?? [], platform), [query.data, platform]);
  const chart = useMemo(() => (entries.length > 0 ? toPoints(entries) : null), [entries]);

  if (query.isPending) {
    return (
      <>
        <SectionLabel>PORTFOLIO HISTORY</SectionLabel>
        <p className="mt-4 font-mono text-xs text-ink-500">กำลังโหลดประวัติ…</p>
      </>
    );
  }

  // ยังไม่มีประวัติเลย — อธิบายว่าทำไม แทนที่จะโชว์กล่องว่าง
  if (entries.length === 0) {
    return (
      <>
        <SectionLabel>PORTFOLIO HISTORY</SectionLabel>
        <p className="mt-3 text-sm text-ink-300">
          {platform === null ? 'ยังไม่มีประวัติย้อนหลัง' : `ยังไม่มีประวัติย้อนหลังของ ${platform}`}
        </p>
        <p className="mt-1.5 text-xs leading-relaxed text-ink-500">
          ระบบบันทึกมูลค่าพอร์ตอัตโนมัติทุกวันเวลา 23:00 น. กราฟจะเริ่มขึ้นหลังมีข้อมูลอย่างน้อย 2
          วัน
        </p>
      </>
    );
  }

  const first = entries[0]!;
  const last = entries[entries.length - 1]!;
  const change = Number(last.valueTHB) - Number(first.valueTHB);
  const changeText = change === 0 ? '0' : String(change);

  return (
    <>
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <SectionLabel>PORTFOLIO HISTORY</SectionLabel>
          {entries.length > 1 && (
            <p className="mt-2 text-xs text-ink-400">
              ตั้งแต่ {formatDate(first.date)} เปลี่ยนไป{' '}
              <span className={`font-mono ${toneClassFor(changeText)}`}>
                {formatSignedTHB(changeText)}
              </span>
            </p>
          )}
        </div>

        <RangePicker days={days} onChange={setDays} />
      </header>

      {entries.length === 1 ? (
        <p className="py-8 text-center text-sm text-ink-500">
          มีข้อมูลวันเดียว ({formatDate(first.date)}) — ต้องมีอย่างน้อย 2 วันจึงจะวาดเส้นได้
        </p>
      ) : (
        <>
          <svg
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            className="mt-4 h-44 w-full"
            preserveAspectRatio="none"
            role="img"
            aria-label={`มูลค่า${platform ?? 'พอร์ต'}ตั้งแต่ ${first.date} ถึง ${last.date}`}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" style={{ stopColor: 'var(--color-up)', stopOpacity: 0.35 }} />
                <stop
                  offset="100%"
                  style={{ stopColor: 'var(--color-brand)', stopOpacity: 0.03 }}
                />
              </linearGradient>
            </defs>

            {/* พื้นที่ใต้เส้น — ช่วยให้เห็นทิศทางจากหางตา ไม่ได้สื่อขนาดของมูลค่า */}
            <polygon
              fill={`url(#${gradientId})`}
              points={[
                `${chart!.points[0]!.x},${HEIGHT}`,
                ...chart!.points.map((p) => `${p.x},${p.y}`),
                `${chart!.points[chart!.points.length - 1]!.x},${HEIGHT}`,
              ].join(' ')}
            />

            <polyline
              points={chart!.points.map((p) => `${p.x},${p.y}`).join(' ')}
              className="stroke-up"
              fill="none"
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />

            {chart!.points.map((p) =>
              // จุดที่ข้อมูลไม่ครบต้องมองเห็นได้ ไม่งั้นหลุมในกราฟจะดูเหมือนพอร์ตร่วงจริง
              // สี่เหลี่ยมไม่ใช่วงกลม ให้เข้ากับภาษาพิกเซลของธีม
              p.entry.isPartial ? (
                <rect
                  key={p.entry.date}
                  x={p.x - 3}
                  y={p.y - 3}
                  width="6"
                  height="6"
                  className="fill-warn"
                />
              ) : null,
            )}
          </svg>

          <div className="mt-2 flex flex-wrap justify-between gap-2 font-mono text-[10px] text-ink-500">
            <span>{formatDate(first.date)}</span>
            <span>
              ต่ำสุด {formatTHB(String(chart!.min))} · สูงสุด {formatTHB(String(chart!.max))}
            </span>
            <span>{formatDate(last.date)}</span>
          </div>

          {entries.some((e) => e.isPartial) && (
            <p className="mt-4 flex items-start gap-2.5 rounded-lg border border-warn/25 bg-warn/10 px-3.5 py-2.5 text-xs leading-relaxed text-warn">
              <span aria-hidden className="mt-1 size-2.5 shrink-0 animate-blink bg-warn" />
              <span>
                จุดสีเหลืองคือวันที่ดึงราคาได้ไม่ครบ มูลค่าวันนั้นจึงต่ำกว่าความจริง —
                ไม่ใช่ว่าพอร์ตลดลง
              </span>
            </p>
          )}
        </>
      )}
    </>
  );
}
